import type { ProcessBase } from "types/process";

/**
 * Format a millisecond duration as "HH:MM:SS" (or "M:SS" for <1 hour).
 * Returns "—" for undefined/negative values.
 */
export function formatDuration(ms?: number | null): string {
  if (!ms || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

/**
 * Convenience: compute elapsed milliseconds for a process using dateCreated → dateUpdated.
 */
export function getElapsedMsFromProcess(
  process: Pick<ProcessBase, "dateCreated" | "dateUpdated">
) {
  return msBetweenIso(process.dateCreated, process.dateUpdated);
}

/**
 * A friendly label explaining what the elapsed time represents.
 * You can customize this if your product terminology changes.
 */
export function getElapsedLabel(): string {
  return "Duration (hh:mm:ss)";
}

/**
 * Compute the non-negative difference (ms) between two ISO strings.
 * Returns undefined if either cannot be parsed.
 */
export function msBetweenIso(
  startIso?: string,
  endIso?: string
): number | undefined {
  if (!startIso || !endIso) return undefined;
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
  return Math.max(0, end - start);
}
